/**
 * ModernHistoryTab - Audit log of admin actions
 */
import React, { useEffect, useState } from 'react';
import { History, Filter, Search, User as UserIcon, Clock3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import AdminCard from './AdminCard';
import AdminCardActions from './AdminCardActions';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useAdmin } from './AdminHooks';
import { adminAPI } from './AdminUtils';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';

const ModernHistoryTab = () => {
  const { user } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState('100');
  const [detailsById, setDetailsById] = useState({});
  const deriveTargetId = (it) => {
    try {
      if (it?.targetId) return it.targetId;
      const p = String(it?.path || '');
      const noQuery = p.split('?')[0];
      const parts = noQuery.split('/').filter(Boolean);
      if (!parts.length) return '';
      const stop = new Set(['reservations', 'orders', 'events', 'media', 'clients', 'notifications', 'stats', 'admins', 'tables', 'history']);
      const last = parts[parts.length - 1];
      if (!stop.has(last)) return last;
      if (parts.length >= 2) {
        const prev = parts[parts.length - 2];
        if (!stop.has(prev)) return prev;
      }
      const m = noQuery.match(/[0-9a-fA-F-]{6,}/);
      return m ? m[0] : '';
    } catch {
      return '';
    }
  };
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(max-width: 768px)').matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(max-width: 768px)');
      const handler = (e) => setIsMobile(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  const [viewMode, setViewMode] = useState('table');

  const tabs = [
    { value: 'all', label: 'Tous' },
    { value: 'reservations', label: 'Réservations' },
    { value: 'orders', label: 'Commandes' },
    { value: 'events', label: 'Événements' },
    { value: 'media', label: 'Médias' },
    { value: 'clients', label: 'Clients' },
    { value: 'notifications', label: 'Emails' },
    { value: 'stats', label: 'Stats' },
    { value: 'admins', label: 'Admins' },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const params = [];
      if (email) params.push(`email=${encodeURIComponent(email)}`);
      if (tab && tab !== 'all') params.push(`tab=${encodeURIComponent(tab)}`);
      const res = await adminAPI.get(`/history${params.length ? `?${params.join('&')}` : ''}`);
      let items = res.data.items || [];
      if (query) {
        const ql = query.toLowerCase();
        items = items.filter((it) => {
          const fields = [it.email, it.method, it.path, it.action, it.tab, it.targetId].filter(Boolean).map(String);
          return fields.some(f => f.toLowerCase().includes(ql));
        });
      }
      if (limit) {
        const lim = parseInt(limit) || 100;
        items = items.slice(0, lim);
      }
      setItems(items);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [email, tab, query, limit]);

  const loadDetails = async (it) => {
    if (!it || detailsById[it.id]) return;
    const tid = deriveTargetId(it);
    let label = null;
    let details = null;
    try {
      if (it.tab === 'reservations' && tid) {
        try {
          const res = await adminAPI.get(`/reservations/${tid}`);
          const r = res.data || {};
          label = `${r.name || ''} (${r.email || ''})`;
          details = `Réservation: ${label}`;
        } catch {}
      } else if (it.tab === 'events' && tid) {
        try {
          const res = await adminAPI.get(`/events/${tid}`);
          const ev = res.data.event || {};
          label = `Vol.${ev.volume} - ${ev.theme || ''}`;
          details = `Événement: ${label}`;
        } catch {}
      }
    } catch {}
    if (!label) {
      const titleMap = {
        reservations: 'Réservation',
        orders: 'Commande',
        events: 'Événement',
        media: 'Média',
        clients: 'Client',
        notifications: 'Email',
        stats: 'Stat',
        admins: 'Admin',
        tables: 'Table',
      };
      const title = titleMap[it.tab] || (it.tab || '');
      label = tid ? `${title}: ${tid}` : String(it.path || '');
    }
    if (!details) {
      details = `${it.method} ${it.path}`;
    }
    setDetailsById(prev => ({ ...prev, [it.id]: { label, details } }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Historique des Actions</h2>
          <p className="text-slate-400">Suivi des opérations effectuées par les administrateurs</p>
        </div>
        <div className="hidden rounded-lg border sm:flex bg-slate-900 border-slate-700">
          <button
            className={`px-3 py-2 text-sm ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-300'}`}
            onClick={() => setViewMode('table')}
          >
            Tableau
          </button>
          <button
            className={`px-3 py-2 text-sm ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-slate-300'}`}
            onClick={() => setViewMode('cards')}
          >
            Cartes
          </button>
        </div>
      </div>

      <Card className="bg-slate-900/40 border-slate-700">
        <CardHeader>
          <CardTitle className="flex gap-2 items-center">
            <History size={18} />
            Historique
          </CardTitle>
          <CardDescription className="text-slate-400">
            {loading ? 'Chargement...' : `${items.length} élément(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2 mb-4 sm:grid-cols-4">
            <div>
              <Input placeholder="Filtrer par email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Select value={tab} onValueChange={setTab}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Onglet" /></SelectTrigger>
                <SelectContent>
                  {tabs.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Input placeholder="Recherche" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <div>
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Limite" /></SelectTrigger>
                <SelectContent>
                  {['50','100','200','500'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {viewMode === 'table' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Onglet</TableHead>
                  <TableHead>Méthode</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Cible</TableHead>
                  <TableHead>Chemin</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => {
                  const when = (() => {
                    try {
                      const d = typeof it.time === 'string' ? new Date(it.time) : new Date(it.time);
                      return d.toLocaleString();
                    } catch {
                      return String(it.time || '');
                    }
                  })();
                  return (
                    <TableRow key={it.id}>
                      <TableCell className="text-slate-300">{when}</TableCell>
                      <TableCell className="text-slate-300">{it.email || '—'}</TableCell>
                      <TableCell>
                        <Badge className="bg-slate-700 text-slate-300 border-slate-600">{it.tab || '—'}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-300">{it.method}</TableCell>
                      <TableCell className="text-slate-300">{it.action || '—'}</TableCell>
                      <TableCell className="text-slate-300">{deriveTargetId(it) || '—'}</TableCell>
                      <TableCell className="text-slate-400">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center underline cursor-help decoration-dotted"
                                onMouseEnter={() => loadDetails(it)}
                              >
                                {it.path}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-white border bg-slate-800 border-slate-700">
                              <div className="space-y-1">
                                {(() => {
                                  const lbl = (detailsById[it.id]?.label) || it.displayLabel;
                                  const det = (detailsById[it.id]?.details) || it.displayDetails;
                                  return (
                                    <>
                                      <div className="text-white">{lbl || 'Données indisponibles'}</div>
                                      {det ? <div className="text-xs text-slate-300">{det}</div> : null}
                                    </>
                                  );
                                })()}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          it.statusCode >= 200 && it.statusCode < 300 ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                          "bg-red-500/20 text-red-400 border-red-500/30"
                        )}>{it.statusCode}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((it) => {
                const when = (() => {
                  try {
                    const d = typeof it.time === 'string' ? new Date(it.time) : new Date(it.time);
                    return d.toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });
                  } catch {
                    return String(it.time || '');
                  }
                })();
                return (
                  <AdminCard key={it.id}>
                    <CardHeader className="pb-3">
                      <div className="flex gap-2 items-center">
                        <Clock3 size={16} className="text-blue-400" />
                        <CardTitle className="text-lg text-white truncate">{when}</CardTitle>
                        <Badge className={cn(
                          it.statusCode >= 200 && it.statusCode < 300 ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                          "bg-red-500/20 text-red-400 border-red-500/30"
                        )}>{it.statusCode}</Badge>
                      </div>
                      <div className="mt-1 text-sm truncate text-slate-300">{it.email || '—'}</div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                          <div className="text-xs tracking-wide uppercase text-slate-400">Contexte</div>
                          <div className="mt-1 text-sm text-slate-300">
                            Onglet: <span className="font-medium">{it.tab || '—'}</span><br />
                            Méthode: <span className="font-medium">{it.method}</span><br />
                            Action: <span className="font-medium">{it.action || '—'}</span><br />
                            Cible: <span className="font-medium">{deriveTargetId(it) || '—'}</span>
                          </div>
                        </div>
                        <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                          <div className="text-xs tracking-wide uppercase text-slate-400">Chemin</div>
                          <div className="mt-1 text-sm truncate text-slate-300">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="underline cursor-help decoration-dotted" onMouseEnter={() => loadDetails(it)}>{it.path}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-white border bg-slate-800 border-slate-700">
                                  <div className="space-y-1">
                                    {(() => {
                                      const lbl = (detailsById[it.id]?.label) || it.displayLabel;
                                      const det = (detailsById[it.id]?.details) || it.displayDetails;
                                      return (
                                        <>
                                          <div className="text-white">{lbl || 'Données indisponibles'}</div>
                                          {det ? <div className="text-xs text-slate-300">{det}</div> : null}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <AdminCardActions>
                      <span className="text-xs text-slate-500">Historique</span>
                    </AdminCardActions>
                  </AdminCard>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernHistoryTab;
